import React from 'react';
import useDialogDismiss from '../../hooks/useDialogDismiss';
import { Link } from 'react-router-dom';
import { Coins, X } from 'lucide-react';

/**
 * TOP-UP MODAL — shown at the moment an action fails on affordability (owner 2026-08-15: "when there is lack
 * of credits, show a modal for user to top-up").
 *
 * An error string under a button was easy to miss and read as a fault; this is the canonical monetization
 * moment (PRODUCT.md Stage 5: the ask happens where intent peaks), so it gets a real surface: what ran out
 * and the one action that fixes it now. Never a dead end — "Not now" always closes it.
 */
export interface TopUpModalProps {
  /** Open the modal — false renders nothing. */
  open: boolean;
  /** What the creator was trying to do, in their words ("draft the story", "render this video"). */
  action?: string;
  onClose: () => void;
}

const TopUpModal: React.FC<TopUpModalProps> = ({ open, action, onClose }) => {
  // This dialog had NO way out from the keyboard at all — a paywall you can only dismiss with a mouse is the
  // worst place in the product to trap someone (BUGS.md 2026-08-30).
  const dialog = useDialogDismiss(open, onClose);
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      {...dialog}
      role="dialog"
      aria-modal="true"
      aria-label="Out of credits"
      data-testid="topup-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-sm rounded-2xl border border-secondary-200 bg-white p-5 shadow-2xl dark:border-secondary-700 dark:bg-secondary-900">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-secondary-100 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-200">
            <Coins className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold text-secondary-900 dark:text-white">
              Out of credits{action ? ` to ${action}` : ''}
            </h2>
            <p className="mt-1 text-sm leading-relaxed text-secondary-500 dark:text-secondary-400">
              Top up now and pick up right where you left off. Purchased credits never expire.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 p-1 text-secondary-400 transition-colors hover:text-secondary-700 dark:hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <Link
            to="/wallet"
            data-testid="topup-modal-plans"
            className="inline-flex min-h-[44px] flex-1 items-center justify-center rounded-xl bg-secondary-900 px-4
                       text-sm font-semibold text-white transition-opacity hover:opacity-90 dark:bg-white dark:text-secondary-900"
          >
            Top up
          </Link>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-[44px] items-center rounded-xl border border-secondary-300 px-4 text-sm
                       font-medium text-secondary-700 transition-colors hover:border-secondary-500
                       dark:border-secondary-700 dark:text-secondary-200 dark:hover:border-secondary-500"
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
};

export default TopUpModal;
